/**
 * useColumnPrefs — per-user column layout (order + width + hidden) for App-mode lists.
 * Per-user, connection/object-scoped prefsStore (same tier as useViewPrefs).
 * `applyTo(base)` layers the prefs over the schema/attribute-derived columns.
 */
import { useCallback, useEffect, useState } from 'react'
import { prefsStore } from '@/app/preferences/prefsStore'
import type { ListColumnSpec } from './listSchema'

export interface ColumnPrefs {
  order: string[]
  widths: Record<string, number>
  hidden: string[]
}

const EMPTY: ColumnPrefs = { order: [], widths: {}, hidden: [] }
const keyFor = (c: string, o: string) => `list_column_prefs:${c}:${o}`

function load(c: string, o: string): ColumnPrefs {
  try {
    const raw = prefsStore.getItem(keyFor(c, o))
    if (!raw) return EMPTY
    const p = JSON.parse(raw) as Partial<ColumnPrefs>
    return {
      order: Array.isArray(p.order) ? p.order.filter((x) => typeof x === 'string') : [],
      widths: p.widths && typeof p.widths === 'object' ? (p.widths as Record<string, number>) : {},
      hidden: Array.isArray(p.hidden) ? p.hidden.filter((x) => typeof x === 'string') : [],
    }
  } catch {
    return EMPTY
  }
}

export function useColumnPrefs(connectionId: string, objectName: string) {
  const [prefs, setPrefs] = useState<ColumnPrefs>(() => load(connectionId, objectName))

  useEffect(() => {
    setPrefs(load(connectionId, objectName))
  }, [connectionId, objectName])

  const persist = useCallback(
    (next: ColumnPrefs) => {
      setPrefs(next)
      try {
        prefsStore.setItem(keyFor(connectionId, objectName), JSON.stringify(next))
      } catch {
        /* private browsing */
      }
    },
    [connectionId, objectName]
  )

  const setOrder = useCallback(
    (names: string[]) => persist({ ...load(connectionId, objectName), order: names }),
    [connectionId, objectName, persist]
  )

  const setWidth = useCallback(
    (name: string, px: number) => {
      const cur = load(connectionId, objectName)
      const widths = { ...cur.widths }
      if (px > 0) widths[name] = px
      else delete widths[name]
      persist({ ...cur, widths })
    },
    [connectionId, objectName, persist]
  )

  const toggleHidden = useCallback(
    (name: string) => {
      const cur = load(connectionId, objectName)
      const hidden = cur.hidden.includes(name)
        ? cur.hidden.filter((n) => n !== name)
        : [...cur.hidden, name]
      persist({ ...cur, hidden })
    },
    [connectionId, objectName, persist]
  )

  const reset = useCallback(() => persist(EMPTY), [persist])

  const setAll = useCallback((p: ColumnPrefs | undefined) => persist(p ?? EMPTY), [persist])

  const applyTo = useCallback(
    (baseCols: ListColumnSpec[]): ListColumnSpec[] => {
      const byName = new Map(baseCols.map((c) => [c.name, c]))
      const ordered: ListColumnSpec[] = []

      // Add columns in the order specified by prefs
      for (const name of prefs.order) {
        const c = byName.get(name)
        if (c) {
          ordered.push(c)
          byName.delete(name)
        }
      }

      // Append new columns that weren't in the order, keeping base order
      for (const c of baseCols) {
        if (byName.has(c.name)) {
          ordered.push(c)
        }
      }

      // Filter hidden and apply widths
      return ordered
        .filter((c) => !prefs.hidden.includes(c.name))
        .map((c) => (prefs.widths[c.name] ? { ...c, width: prefs.widths[c.name] } : c))
    },
    [prefs]
  )

  return { prefs, setOrder, setWidth, toggleHidden, reset, setAll, applyTo }
}
